/* helper.ts */

/* d3.js */
import * as d3 from "d3";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Utils */
import { capitalizeFirstLetter } from "src/utils/helper/string-transformation";
import {
  getCohortGroupColor,
  getStackSegmentColor,
  getStackSegmentColorKey,
  getStackGradientTargets,
} from "src/utils/helper/charts/colorMapper";
import { splitAxisLabel } from "src/utils/helper/charts/helper";

import {
  createChartTooltip,
  ScssProps,
} from "src/utils/helper/chart-tooltip";

/* Constants */
import { basicStyles } from "src/shared/styles/Charts";
import {
  NOT_LOOKED_FOR_LABEL,
  LOOKED_FOR_NO_VALUE_LABEL,
  orderNoValueCategoriesLast,
} from "src/shared/constants/charts/NoValueStates";

/* Interfaces */
import { BarchartProps } from "src/shared/interfaces/charts/Barchart";

import {
  ChartTooltipRef,
  StackedCharts,
} from "src/shared/interfaces/ChartTooltip";

/**
 * Append "Not looked for" / "Looked for, no value" as extra categories to
 * each series' `transformedData`, so they render as additional stacked-bar
 * groups alongside the real categories. Each category is only added (for a
 * given series) when that series' count is strictly greater than 0 — a
 * category still shows up on the shared axis as long as at least one series
 * has a non-zero count for it (the other series then render a 0-height
 * segment there, same as any real category with no data for that series).
 */
export const withNoValueCategories = (
  data: BarchartProps[]
): BarchartProps[] =>
  data.map((series) => {
    const extraCategories = [
      {
        category: NOT_LOOKED_FOR_LABEL,
        value: series.notLookedForCount ?? 0,
      },
      {
        category: LOOKED_FOR_NO_VALUE_LABEL,
        value: series.lookedForNoValueCount ?? 0,
      },
    ].filter((entry) => entry.value > 0);

    return {
      ...series,
      transformedData: [
        ...(series.transformedData ?? []),
        ...extraCategories,
      ],
    };
  });

export const createBarchartCategories = (
  data: BarchartProps[],
  plotWidth: number
): {
  categories: string[];
  xScale: d3.ScaleBand<string>;
} => {
  const categories = orderNoValueCategoriesLast(
    Array.from(
      new Set(
        data.flatMap(
          (d) =>
            d.transformedData?.map((t) => t.category) ?? []
        )
      )
      // Drop a category that is 0 across every visible series - e.g. a feature
      // value that only occurred in a cohort group currently filtered out
      // (each series carries the full category set with 0s, so such a category
      // would otherwise render as an empty labelled bar).
    ).filter((category) =>
      data.some(
        (d) =>
          (d.transformedData?.find(
            (t) => t.category === category
          )?.value ?? 0) > 0
      )
    )
  );

  const xScale = d3
    .scaleBand()
    .domain(categories)
    .range([0, plotWidth])
    .padding(0.25);

  return { categories, xScale };
};

export const computeBarchartStackData = (
  data: BarchartProps[],
  categories: string[],
  subgroups: string[]
): Record<string, number>[] => {
  return categories.map((category) => {
    const categoryValues: Record<string, number> = {};

    subgroups.forEach((group) => {
      const dataset = data.find((d) => d.name === group);
      categoryValues[group] =
        dataset?.transformedData?.find(
          (t) => t.category === category
        )?.value ?? 0;
    });

    return categoryValues;
  });
};

export const sanitizeGradientId = (label: string) =>
  label.replace(/[^a-zA-Z0-9-_]/g, "-");

export const createPathForRoundedCorners = (
  isTop: boolean,
  x: number,
  y: number,
  height: number,
  width: number,
  radius: number
) => {
  if (isTop && height >= radius) {
    return `
        M${x},${y + height} 
        v${-height + radius} 
        a${radius},${radius} 0 0 1 ${radius},${-radius}
        h${width - 2 * radius} 
        a${radius},${radius} 0 0 1 ${radius},${radius}
        v${height - radius} 
        h${-width} 
        z
    `;
  }

  return `
        M${x},${y + height} 
        v${-height} 
        h${width} 
        v${height} 
        z
    `;
};

export const drawAxes = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  xScale: d3.AxisScale<string>,
  yScale: d3.ScaleLinear<number, number>,
  plotHeight: number,
  plotWidth: number,
  unit?: string
) => {
  // x-Axis
  const xAxis = g
    .append("g")
    .attr("transform", `translate(0,${plotHeight})`)
    .call(d3.axisBottom(xScale).tickSize(0))
    .call((g) => g.select(".domain").remove());

  xAxis
    .selectAll("text")
    .attr("y", 16)
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color);

  // y-Axis
  const yAxis = g
    .append("g")
    .call(
      d3
        .axisLeft(yScale)
        .ticks(5)
        .tickSize(-plotWidth)
        .tickPadding(21)
    )
    .call((g) => g.select(".domain").remove());

  yAxis
    .selectAll("text")
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color);

  yAxis
    .selectAll(".tick line")
    .attr("stroke", (d) =>
      d === 0
        ? basicStyles.grid.xAxis.zeroLineColor
        : "#ccc"
    )
    .attr("stroke-width", (d) => (d === 0 ? 1.5 : 1))
    .attr("stroke-dasharray", (d) =>
      d === 0 ? "none" : "3,5"
    );

  const { xLabel, yLabel } = splitAxisLabel(unit);

  // x-Axis (label)
  g.append("text")
    .attr("text-anchor", "middle")
    .attr("x", plotWidth / 2)
    .attr("y", plotHeight + 62)
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color)
    .text(xLabel);

  // y-Axis (label)
  g.append("text")
    .attr("text-anchor", "middle")
    .attr("transform", "rotate(-90)")
    .attr("x", -plotHeight / 2)
    .attr("y", -62)
    .style("font-size", basicStyles.fontSize.unit)
    .style("fill", basicStyles.font.color)
    .text(yLabel);
};

export const createGradients = (
  svg: SVGSelection,
  subgroups: string[]
) => {
  const defs = svg.append("defs");

  getStackGradientTargets(subgroups).forEach((subgroup, index) => {
    const colorBase = getCohortGroupColor(subgroup, index);
    const gradientId = `gradient-${sanitizeGradientId(
      subgroup
    )}`;

    const gradient = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr(
        "stop-color",
        d3.color(colorBase)?.brighter(0.3)?.toString() ??
          colorBase
      );

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr(
        "stop-color",
        d3.color(colorBase)?.darker(0.35)?.toString() ??
          colorBase
      );
  });
};

const setTooltipData = (tooltipData: StackedCharts) => {
  if (!tooltipData) return [];

  const { value, group } = tooltipData;

  return [
    {
      color: tooltipData.color,
      value: capitalizeFirstLetter(tooltipData.label),
    },
    { key: "Group", value: group },
    { key: "Value", value },
  ];
};

export const drawBarsWithRoundedCorners = (
  svg: SVGSelection,
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  stack: d3.Series<Record<string, number>, string>[],
  subgroups: string[],
  categories: string[],
  xScale: d3.ScaleBand<string>,
  yScale: d3.ScaleLinear<number, number>,
  tooltipRef: React.RefObject<ChartTooltipRef>
) => {
  const layers = g
    .selectAll(".layer")
    .data(stack)
    .enter()
    .append("g")
    .attr("class", "layer");

  layers
    .selectAll("path")
    .data((layer, layerIndex) =>
      layer.map((point, categoryIndex) => {
        const label = subgroups[layerIndex];
        const category = categories[categoryIndex];

        const isTop =
          layerIndex === stack.length - 1 ||
          stack
            .slice(layer.index + 1)
            .every(
              (upperLayer) =>
                upperLayer[categoryIndex][1] ===
                upperLayer[categoryIndex][0]
            );

        return {
          ...point,
          label,
          category,
          value: point[1] - point[0],
          gradientId: `gradient-${sanitizeGradientId(
            getStackSegmentColorKey(
              label,
              category,
              subgroups
            )
          )}`,
          isTop,
        };
      })
    )
    .enter()
    .append("path")
    .attr("fill", (d) => `url(#${d.gradientId})`)
    .attr("d", (d) => {
      const x = xScale(d.category) ?? 0;
      const y = yScale(d[1]) ?? 0;
      const width = xScale.bandwidth();

      const ZERO_LINE_OFFSET = -0.825;

      const yBottomRaw = yScale(d[0]) ?? 0;
      const yBottom =
        d[0] === 0
          ? yBottomRaw - ZERO_LINE_OFFSET
          : yBottomRaw;

      const height = Math.max(0, yBottom - y);
      const radius = 8;

      return createPathForRoundedCorners(
        d.isTop,
        x,
        y,
        height,
        width,
        radius
      );
    })
    .on("mousemove", (event: MouseEvent, d) => {
      const target = event.currentTarget as SVGElement;
      d3.select(target).attr("opacity", 0.85);

      const tooltipData: StackedCharts = {
        label: d.label,
        group: d.category,
        value: d.value,
        color:
          d3
            .color(
              getStackSegmentColor(
                d.label,
                d.category,
                subgroups
              )
            )
            ?.toString() ?? "#000",
      };

      const scssProps: ScssProps = {
        margin: -52,
      };

      createChartTooltip(
        tooltipRef,
        () => setTooltipData(tooltipData),
        svg,
        event,
        scssProps
      );
    })
    .on("mouseleave", (event: MouseEvent) => {
      const target = event.currentTarget as SVGElement;
      d3.select(target).attr("opacity", 1);
      tooltipRef.current?.hideTooltip();
    });
};
